import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAccount } from 'wagmi';
import { useAuth } from './AuthContext';

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  const { user } = useAuth();
  const { address, isConnected } = useAccount();

  const [wallets, setWallets] = useState([]);
  const [walletsMax, setWalletsMax] = useState(10);
  const [walletsLoading, setWalletsLoading] = useState(true);

  const refetchWallets = useCallback(async () => {
    if (!user) {
      setWallets([]);
      setWalletsLoading(false);
      return;
    }
    try {
      const res = await fetch('/api/customer/wallets', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setWallets(data.wallets || []);
        setWalletsMax(data.max || 10);
      }
    } catch (err) {
      console.warn('Failed to fetch wallets:', err);
    } finally {
      setWalletsLoading(false);
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    if (user) {
      setWalletsLoading(true);
      refetchWallets();
    } else {
      setWallets([]);
      setWalletsLoading(false);
    }
  }, [user, refetchWallets]);

  // Derived values
  const connectedAddress = address || null;
  const isWagmiConnected = isConnected;
  const connectedIsLinked = !!(connectedAddress && wallets.some(
    w => w.wallet_address.toLowerCase() === connectedAddress.toLowerCase()
  ));
  const primaryWallet = wallets.find(w => w.is_primary === true) || null;
  const atLimit = wallets.length >= walletsMax;

  const isLinked = useCallback((addr) => {
    if (!addr) return false;
    return wallets.some(
      w => w.wallet_address.toLowerCase() === addr.toLowerCase()
    );
  }, [wallets]);

  const value = {
    wallets,
    walletsMax,
    walletsLoading,
    connectedAddress,
    isWagmiConnected,
    connectedIsLinked,
    primaryWallet,
    atLimit,
    isLinked,
    refetchWallets,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}

export default WalletContext;
